import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { AnalyticsService } from '../../../shared/services/analytics.service';
import { AlertsNotificationConfigLogEvent } from '../../../shared/services/analytics.service.types';
import { StoreService } from '../../../shared/services/store.service';
import { AppState } from '../../../shared/store/app-state';
import { AlertConfig } from '../../models/AlertsNotificationConfig';
import { AlertsMailingConfigActions } from '../actions/alerts-mailing-config';
import { AlertsNotificationConfigActions } from '../actions/alerts-notification-config';
import { getAlertConfig } from '../selectors/alerts-notification-config';

@Injectable()
export class AlertNotificationAnalyticsEffects {
    private readonly actions$ = inject(Actions);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly storeService = inject<StoreService<AppState>>(StoreService);

    logAlertNotificationStatusChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AlertsMailingConfigActions.subscriptionEnabled, AlertsMailingConfigActions.subscriptionDisabled),
                map(({ type: eventType }) => eventType === AlertsMailingConfigActions.subscriptionEnabled.type),
                tap((enabled) => this.analyticsService.logAlertSubscriptionStatusChange(enabled)),
            ),
        { dispatch: false },
    );

    logAlertNotificationFrequencyChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AlertsMailingConfigActions.frequencySelected),
                tap(({ frequency }) => this.analyticsService.logFrequencyChange(frequency)),
            ),
        { dispatch: false },
    );

    logAlertsNotificationConfigChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AlertsNotificationConfigActions.alertNotificationToggled),
                switchMap((action) =>
                    combineLatest([
                        of(action),
                        this.storeService.select(getAlertConfig(action.name, action.productType)),
                    ]),
                ),
                tap(([{ isActive }, alertConfig]) => {
                    const alertConfigEvent = this.buildAlertConfigEvent({ ...alertConfig, isActive });
                    this.analyticsService.logAlertsNotificationConfigChanges(alertConfigEvent);
                }),
            ),
        { dispatch: false },
    );

    private buildAlertConfigEvent(alertConfig: AlertConfig): AlertsNotificationConfigLogEvent {
        return {
            productType: alertConfig.productType,
            name: alertConfig.name,
            isEnable: alertConfig.isActive ? 'enabled' : 'disabled',
        };
    }
}
